import * as React from "react";

import * as Sentry from "@sentry/nextjs";

import { FeaturesContext } from "context/featurescontext";
import { UserContext } from "context/usercontext";
import useBridgeSWR from "src/common/hooks/BridgeSWRClient";
import { endpoint } from "src/lib/bridgeClient";
import useStore from "src/store/store";

const MyFeatures = ({ children }) => {
  const config = useStore((state) => state.clientConfig);
  const validCreds = useStore((state) => state.valid);

  const dispatch = validCreds() && config?.bapiURL;

  const user = React.useContext(UserContext);
  const { data: featureElections, error: error } = useBridgeSWR<MyFeatures>(
    dispatch ? endpoint.myOrgFeatureElections : null
  );
  const { data: featureGroups, error: featureGroupsError } = useBridgeSWR<FeatureGroup[]>(
    dispatch ? endpoint.myOrgFeatureGroups : null
  );

  const availableFeatures: MyFeatures = React.useMemo(() => {
    if (!user || !featureElections || !featureGroups) return [];

    // Collect all the groups for the user
    const allGroups = new Set([
      ...(user.custom_groups ? user.custom_groups : []),
      ...(user.system_groups ? user.system_groups : []),
    ]);

    // Determine which features the user has access to based on their group (collect the feature's id)
    const featureIds = featureGroups.filter((group) => allGroups.has(group.usergroup)).map((group) => group.feature_id);

    // Leveraging the features id's, determine which of the organizations elected features the user has access to
    return featureElections.filter((fe) => featureIds.includes(fe.id));
  }, [user, featureElections, featureGroups]);

  if (error) {
    Sentry.captureException(error);
  }
  if (featureGroupsError) {
    Sentry.captureException(featureGroupsError);
  }

  return <FeaturesContext.Provider value={availableFeatures}>{children}</FeaturesContext.Provider>;
};

export default MyFeatures;
